import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const SupplierCatalogueModal = ({ supplierId, isOpen, onClose, onAdded, initialProduct }) => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    // Filters
    const [search, setSearch] = useState('');
    const [filterCategory, setFilterCategory] = useState('Tous');
    const [mode, setMode] = useState('EXISTING'); // 'EXISTING' or 'NEW' or 'EDIT'


    // Single Edit Form Data
    const [formData, setFormData] = useState({
        productId: '',
        name: '',
        sku: '',
        barcode: '',
        supplierRef: '',
        purchasePrice: '',
        leadTime: '',
        categoryId: '',
        description: ''
    });

    const fetchProducts = useCallback(async () => {
        if (!isOpen || initialProduct || !user?.companyId) return;
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                companyId: user.companyId,
                search: search,
                limit: 999
            });

            if (filterCategory !== 'Tous') {
                const cat = categories.find(c => c.name === filterCategory);
                if (cat) queryParams.set('categoryId', cat.id);
            }

            const res = await fetch(`/api/products?${queryParams.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setProducts(data.products || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [isOpen, user?.companyId, search, filterCategory, categories, initialProduct]);

    // Fetch categories only ONCE when modal opens
    useEffect(() => {
        if (isOpen && user?.companyId) {
            fetch(`/api/categories?companyId=${user.companyId}`)
                .then(res => res.ok ? res.json() : [])
                .then(catData => setCategories(catData))
                .catch(err => console.error(err));
        }
    }, [isOpen, user?.companyId]);

    // Fetch products when filters change OR modal opens
    useEffect(() => {
        if (isOpen && !initialProduct) {
            fetchProducts();
        }
    }, [isOpen, filterCategory, search, fetchProducts, initialProduct]);

    // Reset when modal opens
    useEffect(() => {
        if (isOpen) {
            if (initialProduct) {
                setMode('EDIT');
                setFormData({
                    productId: initialProduct.isFromInventory ? initialProduct.id : '',
                    catalogItemId: !initialProduct.isFromInventory ? initialProduct.id : '',
                    name: initialProduct.name,
                    sku: initialProduct.sku,
                    barcode: initialProduct.barcode || '',
                    purchasePrice: (initialProduct.isFromInventory ? initialProduct.ProductSupplier?.purchasePrice : initialProduct.purchasePrice) || '',
                    supplierRef: (initialProduct.isFromInventory ? initialProduct.ProductSupplier?.supplierRef : initialProduct.supplierRef) || '',
                    leadTime: (initialProduct.isFromInventory ? initialProduct.ProductSupplier?.leadTime : initialProduct.leadTime) || '',
                    categoryId: initialProduct.categoryId || '',
                    description: initialProduct.description || ''
                });
            } else {
                setMode('EXISTING');
                setSelectedIds([]);
                setFormData({
                    productId: '', name: '', sku: '', barcode: '', supplierRef: '',
                    purchasePrice: '', leadTime: '', categoryId: '', description: ''
                });
            }
        }
    }, [isOpen, initialProduct]);

    if (!isOpen) return null;

    const handleSingleSubmit = async (e) => {
        e.preventDefault();
        try {
            let url = '';
            let method = 'POST';
            let body = {};

            if (mode === 'EDIT') {
                if (initialProduct.isFromInventory) {
                    url = `/api/suppliers/${supplierId}/products`;
                    body = {
                        productId: initialProduct.id,
                        purchasePrice: formData.purchasePrice ? parseFloat(String(formData.purchasePrice).replace(',', '.')) : null,
                        supplierRef: formData.supplierRef || null,
                        leadTime: formData.leadTime ? parseInt(formData.leadTime) : null
                    };
                } else {
                    url = `/api/suppliers/${supplierId}/catalog-items/${initialProduct.id}`;
                    method = 'PUT';
                    body = {
                        name: formData.name,
                        sku: formData.sku || null,
                        barcode: formData.barcode || null,
                        supplierRef: formData.supplierRef || null,
                        purchasePrice: formData.purchasePrice ? parseFloat(String(formData.purchasePrice).replace(',', '.')) : null,
                        leadTime: formData.leadTime ? parseInt(formData.leadTime) : null,
                        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
                        description: formData.description || null
                    };
                }
            } else if (mode === 'NEW') {
                url = `/api/suppliers/${supplierId}/catalog-items`;
                body = {
                    name: formData.name,
                    sku: formData.sku || null,
                    barcode: formData.barcode || null,
                    supplierRef: formData.supplierRef || null,
                    purchasePrice: formData.purchasePrice ? parseFloat(String(formData.purchasePrice).replace(',', '.')) : null,
                    leadTime: formData.leadTime ? parseInt(formData.leadTime) : null,
                    categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
                    description: formData.description || null,
                    companyId: user.companyId
                };
            }

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                showToast(mode === 'EDIT' ? "Article mis à jour" : "Article créé dans le catalogue", "success");
                onAdded();
                onClose();
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur lors de l'enregistrement", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleBulkSubmit = async () => {
        if (selectedIds.length === 0) return;
        try {
            const res = await fetch(`/api/suppliers/${supplierId}/products/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productIds: selectedIds })
            });

            if (res.ok) {
                showToast(`${selectedIds.length} produits ajoutés au catalogue`, "success");
                onAdded();
                onClose();
            } else {
                const data = await res.json();
                showToast(data.error || "Erreur lors de l'ajout", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`bg-white dark:bg-slate-900 rounded-[2rem] w-full ${initialProduct ? 'max-w-xl' : 'max-w-5xl'} shadow-2xl shadow-primary/10 border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]`}>

                {/* Premium Gradient Header */}
                <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                    <div className="relative z-10 flex justify-between items-start">
                        <div className="flex gap-4 items-center">
                            <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
                                <span className="material-symbols-outlined text-primary text-[28px]">
                                    {initialProduct ? 'edit_square' : (mode === 'NEW' ? 'library_add' : 'inventory_2')}
                                </span>
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                    {initialProduct ? `Modifier l'article` : (mode === 'NEW' ? 'Nouveau spécifique' : 'Ajouter au catalogue')}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1 uppercase tracking-widest opacity-70">
                                    {initialProduct ? (initialProduct.sku || 'Édition rapide') : 'Gestion du catalogue fournisseur'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {!initialProduct && (
                        <div className="flex gap-6 mt-6 relative z-10">
                            {[
                                { id: 'EXISTING', label: "Depuis l'inventaire", icon: 'database' },
                                { id: 'NEW', label: "Nouvel article hors-stock", icon: 'add_card' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setMode(tab.id)}
                                    className={`flex items-center gap-2 text-xs font-black uppercase tracking-[0.15em] pb-3 border-b-2 transition-all ${mode === tab.id
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                                        }`}
                                >
                                    <span className="material-symbols-outlined text-[18px] opacity-70">{tab.icon}</span>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    {(mode === 'NEW' || mode === 'EDIT') ? (
                        <form onSubmit={handleSingleSubmit} className="p-8 space-y-8">
                            {/* Section: Informations de base */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="material-symbols-outlined text-primary/50 text-[18px]">info</span>
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Identification du produit</h4>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="col-span-2">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Nom de l'article *</label>
                                        {mode === 'EDIT' && initialProduct.isFromInventory ? (
                                            <div className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800/40 border-2 border-slate-100 dark:border-slate-800 rounded-2xl flex items-center text-slate-500 font-bold opacity-80">
                                                {formData.name}
                                                <span className="ml-auto text-[9px] bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-md uppercase tracking-tighter">Inventaire</span>
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                required
                                                className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-base font-bold text-slate-900 dark:text-white transition-all placeholder:text-slate-300 dark:placeholder:text-slate-600"
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="Ex: Palette de bois 80x120"
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">SKU Interne</label>
                                        <input
                                            type="text"
                                            className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-sm font-mono font-bold text-slate-700 dark:text-slate-300 transition-all"
                                            value={formData.sku}
                                            onChange={e => setFormData({ ...formData, sku: e.target.value })}
                                            placeholder="SKU-AUTO-01"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Code barre (EAN)</label>
                                        <input
                                            type="text"
                                            className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-sm font-mono font-bold text-slate-700 dark:text-slate-300 transition-all"
                                            value={formData.barcode}
                                            onChange={e => setFormData({ ...formData, barcode: e.target.value })}
                                            placeholder="3660529..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section: Conditions Fournisseur */}
                            <div className="space-y-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="material-symbols-outlined text-emerald-500/50 text-[18px]">local_shipping</span>
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Conditons & Tarifs Fournisseur</h4>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Réf. Fournisseur (Tarif)</label>
                                        <input
                                            type="text"
                                            className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/30 text-sm font-bold text-slate-900 dark:text-white transition-all"
                                            value={formData.supplierRef}
                                            onChange={e => setFormData({ ...formData, supplierRef: e.target.value })}
                                            placeholder="Ex: SUP-REF-99"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1 grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Prix d'Achat (€)</label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    className="w-full h-14 px-5 bg-emerald-50/30 dark:bg-emerald-900/10 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/30 text-center text-lg font-black text-emerald-600 dark:text-emerald-400 transition-all"
                                                    value={formData.purchasePrice}
                                                    onChange={e => setFormData({ ...formData, purchasePrice: e.target.value.replace(',', '.') })}
                                                />
                                                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-black text-emerald-600/30">€</span>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Délai (Jours)</label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    className="w-full h-14 px-5 bg-amber-50/30 dark:bg-amber-900/10 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500/30 text-center text-lg font-black text-amber-600 dark:text-amber-400 transition-all"
                                                    value={formData.leadTime}
                                                    onChange={e => setFormData({ ...formData, leadTime: e.target.value })}
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-amber-600/30 text-[18px]">schedule</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Catégorie</label>
                                        <div className="relative">
                                            <select
                                                className="w-full h-14 px-5 appearance-none bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-sm font-bold text-slate-900 dark:text-white transition-all cursor-pointer"
                                                value={formData.categoryId}
                                                onChange={e => setFormData({ ...formData, categoryId: e.target.value })}
                                            >
                                                <option value="">Sélectionner une catégorie</option>
                                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end gap-4 shrink-0 pt-6">
                                <button type="button" onClick={onClose} className="px-8 py-3 rounded-2xl text-slate-500 font-black uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-slate-800 transition-all text-xs">Annuler</button>
                                <button type="submit" className="px-10 py-3 rounded-2xl bg-primary text-white font-black uppercase tracking-widest hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] text-xs flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">check_circle</span>
                                    {mode === 'EDIT' ? 'Mettre à jour' : 'Ajouter au catalogue'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="flex flex-col h-full min-h-[500px]">
                            {/* Search Bar in Modal */}
                            <div className="p-6 bg-slate-50/30 dark:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800 flex flex-wrap gap-4 items-center">
                                <div className="relative flex-1 min-w-[300px]">
                                    <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[22px]">search_insights</span>
                                    <input
                                        className="w-full h-12 pl-12 pr-4 bg-white dark:bg-slate-900 border-2 border-transparent rounded-2xl text-sm font-medium focus:ring-4 focus:ring-primary/10 focus:border-primary/30 outline-none transition-all shadow-sm"
                                        placeholder="Filtrer l'inventaire par nom, SKU..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                                <div className="relative">
                                    <select
                                        className="h-12 bg-white dark:bg-slate-900 border-2 border-transparent rounded-2xl px-5 pr-10 text-sm font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary/30 outline-none shadow-sm cursor-pointer appearance-none min-w-[200px]"
                                        value={filterCategory}
                                        onChange={e => setFilterCategory(e.target.value)}
                                    >
                                        <option value="Tous">Toutes les catégories</option>
                                        {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                    </select>
                                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">filter_list</span>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-32 gap-4">
                                        <div className="size-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
                                        <p className="text-sm font-black text-slate-400 uppercase tracking-widest animate-pulse">Chargement de l'inventaire...</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-2">
                                        {products.length === 0 ? (
                                            <div className="col-span-2 text-center py-20 bg-slate-50 dark:bg-slate-900/40 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                                                <span className="material-symbols-outlined text-5xl text-slate-300 mb-3">inventory_2</span>
                                                <p className="text-slate-400 italic font-medium">Recherche infructueuse...</p>
                                            </div>
                                        ) : products.map(p => (
                                            <div
                                                key={p.id}
                                                onClick={() => toggleSelect(p.id)}
                                                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center gap-4 ${selectedIds.includes(p.id)
                                                        ? 'bg-primary/5 border-primary shadow-lg shadow-primary/5 scale-[1.01]'
                                                        : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                                                    }`}
                                            >
                                                <div className={`size-6 rounded-lg border-2 flex items-center justify-center transition-all ${selectedIds.includes(p.id) ? 'bg-primary border-primary text-white' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                                                    }`}>
                                                    {selectedIds.includes(p.id) && <span className="material-symbols-outlined text-xs font-black">done</span>}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-black text-slate-900 dark:text-slate-100 leading-tight">{p.name}</div>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">{p.sku}</span>
                                                        <span className="size-1 rounded-full bg-slate-300 dark:bg-slate-700"></span>
                                                        <span className="text-[10px] font-bold text-primary/60 uppercase">{p.category?.name || 'Général'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="shrink-0 p-8 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="bg-primary/10 text-primary px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px]">checklist</span>
                                        {selectedIds.length} sélectionnés
                                    </div>
                                    {selectedIds.length > 0 && products.length > 0 && (
                                        <button
                                            onClick={() => {
                                                if (selectedIds.length === products.length) setSelectedIds([]);
                                                else setSelectedIds(products.map(p => p.id));
                                            }}
                                            className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-primary transition-colors"
                                        >
                                            {selectedIds.length === products.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                                        </button>
                                    )}
                                </div>
                                <div className="flex gap-4">
                                    <button onClick={onClose} className="px-8 py-3 rounded-2xl text-slate-500 font-black uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-slate-800 transition-all text-xs">Annuler</button>
                                    <button
                                        onClick={handleBulkSubmit}
                                        disabled={selectedIds.length === 0}
                                        className="px-10 py-3 rounded-2xl bg-primary text-white font-black uppercase tracking-widest hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 disabled:opacity-50 disabled:scale-100 hover:scale-[1.02] active:scale-[0.98] text-xs flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">add_circle</span>
                                        Importer au catalogue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupplierCatalogueModal;
